import {
  getMetricColorForMetric,
  getMetricType,
  METRIC_TYPE,
} from "@/metrics/metric-display"
import type { MetricRecord } from "@/metrics/runtime"

import {
  MAX_INLINE_LIST_ITEMS,
  OPERATIONAL_CONTEXT_METRIC_IDENTIFIERS,
  OPERATIONAL_CONTEXT_METRIC_MANAGERS,
} from "./runsSummaryConstants"
import { focusTextMatches } from "./runsSummaryFocus"
import { average, formatCount } from "./runsSummaryFormat"
import type {
  CountEntry,
  MetricWindowStats,
  RunsPageSummaryLookups,
  RunsPageSummaryRow,
  RunsSummaryFocus,
} from "./runsSummaryTypes"

export function isBinaryMetricForSummary(
  inferredFromValues: boolean,
  metricRecord?: MetricRecord,
): boolean {
  return metricRecord
    ? getMetricType(metricRecord) === METRIC_TYPE.BINARY
    : inferredFromValues
}

function parseRowTime(row: RunsPageSummaryRow): number | null {
  if (!row.created_at) {
    return null
  }
  const time = Date.parse(row.created_at)
  return Number.isFinite(time) ? time : null
}

export function getRowsForWindow(
  rows: RunsPageSummaryRow[],
  startTimeMs: number,
  endTimeMs: number,
): RunsPageSummaryRow[] {
  return rows.filter((row) => {
    const time = parseRowTime(row)
    return time !== null && time >= startTimeMs && time < endTimeMs
  })
}

function getRowMetricValues(row: RunsPageSummaryRow): Record<string, number> {
  return {
    ...(row.metric_averages ?? {}),
    ...(row.metric_values ?? {}),
  }
}

function getRowMetricIds(row: RunsPageSummaryRow): string[] {
  return Array.from(
    new Set([
      ...Object.keys(getRowMetricValues(row)),
      ...(row.metric_ids ?? []),
    ]),
  )
}

export function getRowNoun(rows: RunsPageSummaryRow[]): string {
  return rows.some((row) => row.simulation_output_id) ? "simulation" : "run"
}

export function getName(
  id: string | null | undefined,
  lookup: Record<string, string> | undefined,
  fallback: string,
): string | null {
  if (!id) {
    return null
  }
  return lookup?.[id] ?? id ?? fallback
}

export function incrementCount(
  counts: Map<string, CountEntry>,
  id: string | null | undefined,
  label: string | null,
): void {
  if (!id || !label) {
    return
  }
  const existing = counts.get(id)
  if (existing) {
    existing.count += 1
    return
  }
  counts.set(id, { id, label, count: 1 })
}

export function getTopEntries(counts: Map<string, CountEntry>, limit: number) {
  return [...counts.values()]
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, limit)
}

export function formatCountEntries(entries: CountEntry[]): string {
  return entries
    .map((entry) => `${entry.label} (${formatCount(entry.count)})`)
    .join(", ")
}

export function countEntities(
  rows: RunsPageSummaryRow[],
  selector: (row: RunsPageSummaryRow) => string | null | undefined,
  lookup: Record<string, string> | undefined,
): Map<string, CountEntry> {
  const counts = new Map<string, CountEntry>()
  rows.forEach((row) => {
    const id = selector(row)
    incrementCount(counts, id, getName(id, lookup, "Unknown"))
  })
  return counts
}

export function getMetricDisplayName(
  metricId: string,
  lookups: RunsPageSummaryLookups,
): string {
  const metricRecord = lookups.metricRecordsById?.[metricId]
  return (
    lookups.metricDisplayNames?.[metricId] ??
    metricRecord?.metric_display_name ??
    metricRecord?.metric_name ??
    metricId
  )
}

function normalizeMetricIdentifier(value: string | null | undefined): string {
  return (value ?? "").toLowerCase().replace(/[^a-z0-9]/g, "")
}

export function isOperationalContextMetric(metric: MetricWindowStats): boolean {
  if (
    metric.metricRecord?.metric_manager &&
    OPERATIONAL_CONTEXT_METRIC_MANAGERS.has(metric.metricRecord.metric_manager)
  ) {
    return true
  }

  return [metric.id, metric.label, metric.metricRecord?.metric_name].some(
    (value) =>
      OPERATIONAL_CONTEXT_METRIC_IDENTIFIERS.has(
        normalizeMetricIdentifier(value),
      ),
  )
}

export function formatOperationalMetricList(
  metrics: MetricWindowStats[],
): string {
  return [...metrics]
    .filter((metric) => metric.currentCount > 0)
    .sort((a, b) => b.currentCount - a.currentCount)
    .slice(0, MAX_INLINE_LIST_ITEMS)
    .map((metric) => metric.label)
    .join(", ")
}

export function metricMatchesFocus(
  metricId: string,
  label: string,
  focus: RunsSummaryFocus,
): boolean {
  return focusTextMatches(metricId, focus) || focusTextMatches(label, focus)
}

export function countMetrics(
  rows: RunsPageSummaryRow[],
  lookups: RunsPageSummaryLookups,
): Map<string, CountEntry> {
  const counts = new Map<string, CountEntry>()
  rows.forEach((row) => {
    getRowMetricIds(row).forEach((metricId) => {
      incrementCount(counts, metricId, getMetricDisplayName(metricId, lookups))
    })
  })
  return counts
}

export function getNewEntries(
  currentCounts: Map<string, CountEntry>,
  previousCounts: Map<string, CountEntry>,
  limit: number,
): CountEntry[] {
  return getTopEntries(currentCounts, currentCounts.size)
    .filter((entry) => !previousCounts.has(entry.id))
    .slice(0, limit)
}

function normalizeStatus(status: string | null | undefined): string {
  return (status ?? "").trim().toLowerCase()
}

function isInProgressStatus(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status)
  return (
    normalized.includes("progress") ||
    normalized.includes("running") ||
    normalized.includes("queued") ||
    normalized.includes("pending")
  )
}

function isSuccessfulStatus(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status)
  return (
    normalized === "completed" ||
    normalized === "complete" ||
    normalized === "success" ||
    normalized === "succeeded" ||
    normalized === "passed"
  )
}

function isUnsuccessfulStatus(status: string | null | undefined): boolean {
  const normalized = normalizeStatus(status)
  return (
    Boolean(normalized) &&
    !isSuccessfulStatus(status) &&
    !isInProgressStatus(status)
  )
}

export function isUnsuccessfulRow(row: RunsPageSummaryRow): boolean {
  return (
    isUnsuccessfulStatus(row.error_status) || isUnsuccessfulStatus(row.status)
  )
}

function getMetricValues(
  rows: RunsPageSummaryRow[],
  metricId: string,
): number[] {
  return rows
    .map((row) => getRowMetricValues(row)[metricId])
    .filter(
      (value): value is number => typeof value === "number" && isFinite(value),
    )
}

export function getRowsWithMetricValue(
  rows: RunsPageSummaryRow[],
  metricId: string,
): RunsPageSummaryRow[] {
  return rows.filter((row) => {
    const value = getRowMetricValues(row)[metricId]
    return typeof value === "number" && isFinite(value)
  })
}

function isBinaryLike(values: number[]): boolean {
  return (
    values.length > 0 && values.every((value) => value === 0 || value === 1)
  )
}

function countDangerValues(
  values: number[],
  metricRecord: MetricRecord | undefined,
  isBinary: boolean,
): number {
  if (!metricRecord) {
    return 0
  }
  return values.filter((value) => {
    try {
      return getMetricColorForMetric(value, metricRecord, isBinary) === "danger"
    } catch {
      return false
    }
  }).length
}

export function buildMetricWindowStats(
  currentRows: RunsPageSummaryRow[],
  previousRows: RunsPageSummaryRow[],
  lookups: RunsPageSummaryLookups,
): MetricWindowStats[] {
  const metricIds = new Set<string>()
  currentRows.forEach((row) =>
    getRowMetricIds(row).forEach((id) => metricIds.add(id)),
  )
  previousRows.forEach((row) =>
    getRowMetricIds(row).forEach((id) => metricIds.add(id)),
  )

  return [...metricIds].map((metricId) => {
    const currentValues = getMetricValues(currentRows, metricId)
    const previousValues = getMetricValues(previousRows, metricId)
    const metricRecord = lookups.metricRecordsById?.[metricId]
    const inferredBinary = isBinaryLike(currentValues.concat(previousValues))
    const binaryLike = isBinaryMetricForSummary(inferredBinary, metricRecord)
    return {
      id: metricId,
      label: getMetricDisplayName(metricId, lookups),
      currentAverage: average(currentValues),
      previousAverage: average(previousValues),
      currentCount: currentValues.length,
      previousCount: previousValues.length,
      currentDangerCount: countDangerValues(
        currentValues,
        lookups.metricRecordsById?.[metricId],
        binaryLike,
      ),
      isBinaryLike: binaryLike,
      metricRecord,
    }
  })
}
